import React, { FC, useEffect, useState } from 'react';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { feeDataTypes } from '../../store/merchnts/merchant-context-variables';
import { IFees } from '../../store/merchnts/merchant-interfaces';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { WhisperComponent } from '../developers/page-components/WhisperComponent';
import { useTranslation } from 'react-i18next';

import '../merchants/FeeTableRow.scss';

enum FEE_NAMES {
	RISK_FEE = 'Risk Fee',
	PERCENTAGE = 'Percentage',
	FEE_AMOUNT = 'Fee Amount',
	REFUND_FEE = 'Refund Fee',
	CHARGEBACK_FEE = 'Chargeback Fee',
}

interface Props {
	currentFee: IFees;
	onFeeUpdate: (currentFee: IFees, index: number) => void;
	index: number;
	setDefault?: boolean;
}

export const FeeConfigurationTableRow: FC<Props> = (props) => {
	const { currentFee, onFeeUpdate, index, setDefault } = props;

	const { t } = useTranslation();

	const { merchantState: { allDefaultFees }} = useMerchants();
	const [fee, setFee] = useState(currentFee);

	const isPercentage = (feeName): boolean => feeName === feeDataTypes.PERCENTAGE || feeName === feeDataTypes.RISK_FEE;

	useEffect(() => {
		setFee(currentFee);
	}, [currentFee]);

	const handleFeeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = Math.abs(Number(e.target.value));
		setFee(prev => ({ ...prev, amount: value }));
		onFeeUpdate({ ...fee, amount: value }, index);
	};

	const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		if (e.target.value) {
			let value = Math.abs(Number(e.target.value));
			if (value > 100) {
				value = value - (value - 100);
			}
			setFee(prev => ({ ...prev, amount: value }));
			onFeeUpdate({ ...fee, amount: value }, index);
		} else {
			setFee(prev => ({ ...prev, amount: 0 }));
			onFeeUpdate({ ...fee, amount: 0 }, index);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>, name: string): void =>
		(isPercentage(name)
			? handlePercentageChange(e)
			: handleFeeChange(e));

	const handleDefaultClick = (name): void => {
		const res = allDefaultFees?.find(fee => fee?.name === name);
		setFee(prev => ({ ...prev, amount: res?.amount }));
		onFeeUpdate({ ...fee, amount: res?.amount }, index);
	};

	const handleMinFeeChange = (e): void => {
		const value = e.target.value === '' ? '' : Math.abs(Number(e.target.value));

		setFee(prev => ({ ...prev, minimumFeeAmount: value }));
		onFeeUpdate({ ...fee, minimumFeeAmount: value }, index);
	};

	const getTooltipText = (): string => {
		switch (fee?.name) {
			case FEE_NAMES.PERCENTAGE: return t('DEFAULT_FEE_CONFIG.PERCENTAGE_DESCRIPTION');
			case FEE_NAMES.FEE_AMOUNT: return t('DEFAULT_FEE_CONFIG.FEE_AMOUNT_DESCRIPTION');
			case FEE_NAMES.REFUND_FEE: return t('DEFAULT_FEE_CONFIG.REFUND_FEE_DESCRIPTION');
			case FEE_NAMES.CHARGEBACK_FEE: return t('DEFAULT_FEE_CONFIG.CHARGEBACK_FEE_DESCRIPTION');
		}
	};

	return (
		<tr>
			<td className="w-200px">
				<span className="me-2">{fee?.name}</span>
				{
					fee?.name !== FEE_NAMES.RISK_FEE && (
						<WhisperComponent
							isTooltip
							whisperText={getTooltipText()}
							mainText={
								<FontAwesomeIcon
									icon={faQuestionCircle}
									className="cursor-pointer"
									width={12}
								/>
							}
						/>
					)
				}
			</td>
			<td className="py-7">
				<div className="fee-input-container isEditBG">
					{!isPercentage(fee?.name) && <div>$</div>}
					<input
						type="number"
						className="number-input"
						step="1"
						lang="en"
						placeholder="0.00"
						value={fee?.amount ? fee?.amount : null}
						onChange={(e): void => handleChange(e, fee?.name)}
					/>
					<div>{!isPercentage(fee?.name) ? 'USD' : '%'}</div>
				</div>
			</td>
			<td className="py-7">
				<div className="fee-input-container isEditBG">
					<div>$</div>
					<input
						type="number"
						className="number-input"
						step="1"
						lang="en"
						placeholder="---"
						value={fee?.minimumFeeAmount}
						onChange={(e): void => handleMinFeeChange(e)}
					/>
					<div>USD</div>
				</div>
			</td>
			{
				setDefault && (
					<td>
						<div
							className="text-danger fw-bolder text-decoration-underline cursor-pointer"
							onClick={(): void => handleDefaultClick(fee?.name)}
						>
							Reset to default
						</div>
					</td>
				)
			}
		</tr>
	);
};
